import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import Sidebar from "~/components/Sidebar/Sidebar.tsx";
import { QUOTA_DETAILS_PATH_WITH_ORIGIN } from "~/constants/appRoutes.ts";
import { processedInvoiceQueries } from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import { orderQueries } from "~/modules/project/api/order/orderQueries.ts";
import { PROJECT_QUERY_KEY } from "~/modules/project/api/project/projectQueries.ts";
import { PROJECTS_PHASES_QUERY_KEY } from "~/modules/project/api/projectPhase/projectPhaseQueries.ts";
import { quotaQueries } from "~/modules/quota/api/quota/quotaQueries.ts";
import type { Quota, QuotaType } from "~/modules/quota/api/quota/quotaTypes.ts";
import {
	CreateAdvanceQuotaForm,
} from "~/modules/quota/components/CreateQuotaSidebar/components/CreateAdvanceQuotaForm.tsx";
import {
	CreateInvoiceBackedQuotaForm,
} from "~/modules/quota/components/CreateQuotaSidebar/components/CreateInvoiceBackedQuotaForm.tsx";
import {
	QuotaCompanyAndTypeSelector,
} from "~/modules/quota/components/CreateQuotaSidebar/components/QuotaCompanyAndTypeSelector.tsx";
import { validQuotaDetailsNavigatedFromOrigins } from "~/modules/quota/components/QuotaDetailsView/quotaDetailsUtils.ts";

type Props = {
	isOpen: boolean;
	onClose: () => void;
};

export const CreateQuotaSidebar = ({
	isOpen,
	onClose,
}: Props) => {
	const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
	const [selectedQuotaType, setSelectedQuotaType] = useState<QuotaType | undefined>("advance");
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const handleQuotaCreationSuccess = useCallback(async (quota: Quota, projectIds: string[]) => {
		await queryClient.refetchQueries({ queryKey: quotaQueries.index().queryKey });
		void queryClient.invalidateQueries({ queryKey: quotaQueries.indexByCompany(selectedCompanyId).queryKey });
		// An advance quota creates its advance invoice. An invoice-backed quota links the selected invoices,
		// so they leave the eligible list.
		void queryClient.invalidateQueries({ queryKey: quotaQueries.eligibleInvoices(selectedCompanyId).queryKey });
		void queryClient.invalidateQueries({ queryKey: processedInvoiceQueries.baseKey });

		// Each assigned consumer belongs to a project. Refresh that project's phases, orders and details.
		projectIds.forEach((projectId) => {
			void queryClient.invalidateQueries({ queryKey: PROJECTS_PHASES_QUERY_KEY(projectId) });
			void queryClient.invalidateQueries({ queryKey: orderQueries.byProject(projectId).queryKey });
			void queryClient.invalidateQueries({ queryKey: PROJECT_QUERY_KEY(projectId) });
		});

		onClose();
		navigate(QUOTA_DETAILS_PATH_WITH_ORIGIN(selectedCompanyId, quota.id, validQuotaDetailsNavigatedFromOrigins.QUOTAS_INDEX));
	}, [selectedCompanyId, onClose, queryClient, navigate]);

	const handleClose = () => {
		onClose();
		setTimeout(() => {
			setSelectedQuotaType(undefined);
		}, 1000);
	};

	const handleCompanyAndTypeSubmit = (companyId: string, type: QuotaType) => {
		setSelectedQuotaType(type);
		setSelectedCompanyId(companyId);
	};

	const showCompanyAndTypeSelector = !selectedCompanyId || !selectedQuotaType;
	const showAdvanceQuotaForm = !showCompanyAndTypeSelector && selectedQuotaType === "advance";
	const showInvoiceBackedQuotaForm = !showCompanyAndTypeSelector && selectedQuotaType === "invoice-backed";

	return (
		<Sidebar closeOnOutsideClick={false}
				 open={isOpen}
				 width="xl"
				 setOpen={handleClose}>
			{showCompanyAndTypeSelector && <QuotaCompanyAndTypeSelector onCancel={handleClose}
																		onSubmit={handleCompanyAndTypeSubmit} />}
			{showAdvanceQuotaForm && <CreateAdvanceQuotaForm companyId={selectedCompanyId}
															  onCancel={handleClose}
															  onSuccess={handleQuotaCreationSuccess} />}
			{showInvoiceBackedQuotaForm && <CreateInvoiceBackedQuotaForm companyId={selectedCompanyId}
																		 onCancel={handleClose}
																		 onSuccess={handleQuotaCreationSuccess} />}
		</Sidebar>
	);
};
